import { useState } from "react";
import { useRuntimeConstants } from "../../../context/runtimeConstants/RuntimeConstantsContext";
import eventBusEmitter from "../../../utils/eventBusEmitter";
import { fireProductSummaryFeedbackSubmitted } from "../../../utils/marketingTracking/marketingTrackingEvents";
import { ReturnNotesSummary } from "../../../api/returnNotesSummary";

const EXPERIMENT_VARIANT = "treatment";

export const RETURN_NOTES_SUMMARY_FEEDBACK = {
  UP: "up",
  DOWN: "down",
} as const;

export const RETURN_NOTES_SUMMARY_FEEDBACK_SHELL_ID =
  "return-notes-feedback-shell";

export type ReturnNotesSummaryFeedback =
  (typeof RETURN_NOTES_SUMMARY_FEEDBACK)[keyof typeof RETURN_NOTES_SUMMARY_FEEDBACK];

type RuntimeConstantsShape = {
  product: { id: number | string };
  features: {
    returnNotesSummaryFeedbackEnabled: boolean;
  };
  getTranslation: (translationKey: string) => string;
};

type UseReturnNotesSummaryFeedbackArgs = {
  returnNotesSummary: ReturnNotesSummary | false;
};

const useReturnNotesSummaryFeedback = ({
  returnNotesSummary,
}: UseReturnNotesSummaryFeedbackArgs) => {
  const {
    product,
    features: { returnNotesSummaryFeedbackEnabled },
    getTranslation,
  } = useRuntimeConstants() as RuntimeConstantsShape;

  const [selectedFeedback, setSelectedFeedback] =
    useState<ReturnNotesSummaryFeedback | null>(null);

  const submitFeedback = (feedback: ReturnNotesSummaryFeedback) => {
    if (selectedFeedback) {
      return;
    }

    setSelectedFeedback(feedback);

    fireProductSummaryFeedbackSubmitted({
      product,
      productSummaryVersion: (returnNotesSummary as ReturnNotesSummary).version,
      experimentVariant: EXPERIMENT_VARIANT,
      feedbackPositive: feedback === RETURN_NOTES_SUMMARY_FEEDBACK.UP,
    });

    eventBusEmitter("notificationBar", {
      type: "success",
      copy: getTranslation(
        "pdp_ratings_reviews_return_reason_summary_feedback_given_toast",
      ),
      duration: 3000,
      refocusId: RETURN_NOTES_SUMMARY_FEEDBACK_SHELL_ID,
    });
  };

  return {
    isEnabled: returnNotesSummaryFeedbackEnabled,
    selectedFeedback,
    onThumbsUp: () => submitFeedback(RETURN_NOTES_SUMMARY_FEEDBACK.UP),
    onThumbsDown: () => submitFeedback(RETURN_NOTES_SUMMARY_FEEDBACK.DOWN),
  };
};

export default useReturnNotesSummaryFeedback;
